/**
 * Citation model shared by chips, the source panel and the coverage meter.
 *
 * Sources of a cite (all written by the pipeline):
 *   extracted_data._evidence_spans[key]   {chunk_id, page, quote, kind?, student_file_id?, file_name?}
 *   extracted_data.field_source_pages[key] [pages]   (page-only fallback)
 *   extracted_data._derived_from[key]     [{field, section, pages}]  (calculated values)
 *   row._source                           {page, pages, quote, chunk_id}  (table rows / subtests)
 */

export const CITE_KINDS = {
  verified: {
    label: 'Quote verified',
    hint: 'The value was found word-for-word on this page.',
    chip: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300',
    icon: 'check',
  },
  form: {
    label: 'Read from form',
    hint: 'Read off the form on this page (box or field), not from running text.',
    chip: 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800/60 dark:bg-indigo-950/40 dark:text-indigo-300',
    icon: 'form',
  },
  label: {
    label: 'Form question',
    hint: 'This page asks the question; the answer is the box marked there.',
    chip: 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800/60 dark:bg-indigo-950/40 dark:text-indigo-300',
    icon: 'form',
  },
  page: {
    label: 'Page only',
    hint: 'Located on this page; no exact quote was stored.',
    chip: 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300',
    icon: 'page',
  },
  // Not a formula (client 2026-10-29: the "ƒ Calculated" chip read as one): the value is not
  // printed as such; the fields it rests on are listed with their pages and printed lines.
  derived: {
    label: 'From other fields',
    hint: 'Not printed as one value; it follows from the fields below - open their pages to check.',
    chip: 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300',
    icon: 'page',
  },
  missing: {
    label: 'No source found',
    hint: 'No page in this IEP cycle shows this value — please review.',
    chip: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300',
    icon: 'warn',
  },
};

const toPage = (v) => {
  const n = Number(v && typeof v === 'object' ? v.page : v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

export function uniqPages(list) {
  return [...new Set((list || []).map(toPage).filter(Boolean))].sort((a, b) => a - b);
}

function firstSpan(raw) {
  if (Array.isArray(raw)) return raw.find((s) => s && typeof s === 'object') || null;
  return raw && typeof raw === 'object' ? raw : null;
}

// The vision reader's page reading is stored as text too ('[FORM JSON] { "page_type": ... }');
// a quote taken from it shows JSON ('"name": "Jill", "signed": true'), not the page.
const MARKUP_QUOTE = /"\s*:\s*(?:["[{]|true\b|false\b|null\b|-?\d)|\{\s*"|\[\{|\[(?:TABLE|FORM|ASSESSMENT|NLP|IMAGE|OCR|PAGE)\b[^\]]*\]/;

/** The quote when it is printed text; null for reader markup (the citation keeps its page). */
export function printedQuote(quote) {
  if (typeof quote !== 'string' || !quote.trim()) return null;
  return MARKUP_QUOTE.test(quote) ? null : quote;
}

function kindOfSpan(span) {
  if (!span) return 'page';
  if (span.kind === 'form' || span.kind === 'label') return span.kind;
  return printedQuote(span.quote) ? 'verified' : 'page';
}

/** page → first stored quote on that page (a field can be cited on several pages). */
function quotesByPage(spans) {
  const out = {};
  (spans || []).forEach((s) => {
    const p = toPage(s);
    if (p && printedQuote(s.quote) && !out[p]) out[p] = s.quote;
  });
  return out;
}

/** The quote to mark on ``page``: that page's own quote; null when only other pages have one. */
export function quoteForPage(cite, page) {
  const quotes = cite?.quotes;
  if (quotes && Object.keys(quotes).length) return quotes[page] || null;
  return cite?.quote || null;
}

/** First cited page that has a stored quote (to point the reader there). */
export function firstQuotePage(cite) {
  const quotes = cite?.quotes || {};
  return (cite?.pages || []).find((p) => quotes[p]) || Number(Object.keys(quotes)[0]) || null;
}

/**
 * Cite for one field of a section's extracted_data (first key that has one wins).
 * Retrieval page dumps (> 4 pages, no span) are not shown as a cite.
 */
export function resolveFieldCite(data, keys) {
  const spans = (data && typeof data._evidence_spans === 'object' && data._evidence_spans) || {};
  const pagesMap = (data && typeof data.field_source_pages === 'object' && data.field_source_pages) || {};
  const derivedMap = (data && typeof data._derived_from === 'object' && data._derived_from) || {};
  for (const key of keys.filter(Boolean)) {
    // A span with no quote that was never verified is a chunk the model was shown, not
    // evidence (K. Anderton eligibility team -> Testing Request p.1): not a cite.
    const isEvidence = (sp) => sp && typeof sp === 'object' && !(sp.verified === false && !sp.quote);
    const raw = Array.isArray(spans[key]) ? spans[key].filter(isEvidence) : isEvidence(spans[key]) ? spans[key] : null;
    const span = firstSpan(raw);
    const spanList = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const spanPages = uniqPages(spanList);
    const fieldPages = uniqPages(Array.isArray(pagesMap[key]) ? pagesMap[key] : [pagesMap[key]]);
    const pages = spanPages.length ? spanPages : fieldPages.length <= 4 ? fieldPages : [];
    if (pages.length) {
      return {
        key,
        kind: spanPages.length ? kindOfSpan(span) : 'page',
        pages,
        quote: printedQuote(span?.quote),
        quotes: quotesByPage(spanList),
        fileId: span?.student_file_id ?? span?.file_id ?? null,
        fileName: span?.file_name ?? span?.source_file ?? null,
      };
    }
    const derived = derivedMap[key];
    if (Array.isArray(derived) && derived.length) {
      return {
        key,
        kind: 'derived',
        pages: uniqPages(derived.flatMap((d) => d.pages || [])),
        inputs: derived,
        quote: null,
        fileId: null,
        fileName: null,
      };
    }
  }
  return { key: keys[0], kind: 'missing', pages: [], quote: null, fileId: null, fileName: null };
}

/** Cite stamped on a table row / subtest by the pipeline (``_source``). */
export function rowCite(row) {
  const src = row && typeof row === 'object' ? row._source : null;
  const page = toPage(src);
  if (!page) return null;
  return {
    kind: printedQuote(src.quote) ? 'verified' : 'page',
    pages: uniqPages(src.pages && src.pages.length ? [page, ...src.pages] : [page]).slice(0, 4),
    page,
    quote: printedQuote(src.quote),
    quotes: printedQuote(src.quote) ? { [page]: src.quote } : {},
    fileId: src.student_file_id ?? src.file_id ?? null,
    fileName: src.file_name ?? src.source_file ?? null,
  };
}

/**
 * The one file a section was read from - or nothing when it was read from several: a
 * section default opened the same PDF for every chip without its own file (K. Anderton:
 * the 2024 notice of meeting and the eligibility team opened the wrong documents).
 */
export function sectionDefaultFile(meta) {
  const files = Array.isArray(meta?.files) ? meta.files.filter(Boolean) : [];
  if (files.length > 1) return { id: null, name: null };
  const only = files[0] || {};
  return {
    id: only.student_file_id ?? only.file_id ?? meta?.file_id ?? null,
    name: only.file_name ?? meta?.file_name ?? null,
  };
}

/** "p.51" / "p.51 +2" */
export function pagesLabel(pages) {
  if (!pages?.length) return '';
  return pages.length === 1 ? `p.${pages[0]}` : `p.${pages[0]} +${pages.length - 1}`;
}

export function humanizeKey(key) {
  return String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ---------- highlight geometry ---------- */

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9%<>.]+/g, ' ').trim();

/**
 * Rectangles (in PDF points) covering the quote on a born-digital page.
 * Picks the run of page words that matches the most quote tokens in order.
 */
export function quoteRects(words, quote) {
  const q = norm(quote).split(/\s+/).filter(Boolean).slice(0, 24);
  if (!q.length || !Array.isArray(words) || !words.length) return [];
  // Sub-tokens with their word index: "03/08/2013" on the page is one word but three
  // quote tokens, so matching runs on sub-tokens and maps back to words.
  const toks = [];
  words.forEach((w, wi) => {
    norm(w.t).split(/\s+/).filter(Boolean).forEach((t) => toks.push({ t, wi }));
  });
  // OCR on scans misreads letters ("Atwool", "Stephanle"): a shared 4-letter stem counts.
  const same = (a, b) => a === b
    || (a.length >= 3 && b.length >= 3 && (a.includes(b) || b.includes(a)))
    || (a.length >= 5 && b.length >= 5 && a.slice(0, 4) === b.slice(0, 4));
  let best = { score: 0, start: -1, end: -1 };
  for (let i = 0; i < toks.length; i += 1) {
    if (!same(toks[i].t, q[0])) continue;
    let qi = 0;
    let j = i;
    let score = 0;
    while (j < toks.length && qi < q.length && j - i < q.length + 6) {
      if (same(toks[j].t, q[qi])) {
        score += 1;
        qi += 1;
      }
      j += 1;
    }
    if (score > best.score) best = { score, start: i, end: j };
    if (score === q.length) break;
  }
  const needed = q.length === 1 ? 1 : Math.max(2, Math.ceil(q.length * 0.5));
  if (best.start < 0 || best.score < needed) return [];
  const wordIdx = [...new Set(toks.slice(best.start, best.end).map((x) => x.wi))];
  const hit = wordIdx.map((wi) => words[wi]);
  // One rect per printed line.
  const lines = [];
  hit.forEach((w) => {
    const line = lines.find((l) => Math.abs(l.top - w.top) < 4);
    if (line) {
      line.x0 = Math.min(line.x0, w.x0);
      line.x1 = Math.max(line.x1, w.x1);
      line.bottom = Math.max(line.bottom, w.bottom);
    } else {
      lines.push({ x0: w.x0, x1: w.x1, top: w.top, bottom: w.bottom });
    }
  });
  return lines;
}

/**
 * Best highlight for a cite on one page, as rects in the page's own unit
 * ({unit: 'pt'} for born-digital words, {unit: 'frac'} for OCR words / checkboxes).
 * Tries the stored quote, then the field value ("form" quotes look like
 * "notice date: 03/25/2025", so their value part is tried too), then checkboxes.
 */
export function highlightRects(geo, { quote, value, label }) {
  const texts = [];
  const add = (t) => { const s = String(t || '').trim(); if (s && !texts.includes(s)) texts.push(s); };
  add(quote);
  if (quote && String(quote).includes(':')) add(String(quote).split(':').slice(1).join(':'));
  add(value);
  for (const t of texts) {
    const pt = quoteRects(geo?.words, t);
    if (pt.length) return { unit: 'pt', rects: pt, how: 'quote' };
    const fr = quoteRects(geo?.ocr_words, t);
    if (fr.length) return { unit: 'frac', rects: fr, how: 'quote' };
  }
  const boxes = checkboxMatches(geo?.checkboxes, [value, label, quote]);
  if (boxes.length) {
    return {
      unit: 'frac',
      rects: boxes.map((b) => ({ x0: b.bbox[0], top: b.bbox[1], x1: b.bbox[2], bottom: b.bbox[3] })),
      how: 'checkbox',
    };
  }
  return { unit: 'pt', rects: [], how: null };
}

/** Checkbox boxes (0–1 page fractions) whose printed label matches the field / value. */
export function checkboxMatches(checkboxes, hints) {
  if (!Array.isArray(checkboxes) || !checkboxes.length) return [];
  const want = hints.map(norm).filter((h) => h.length >= 3);
  if (!want.length) return [];
  return checkboxes.filter((b) => {
    const label = norm(b.label);
    return label && want.some((h) => label.includes(h) || h.includes(label));
  });
}
